import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import { putFileWithProgress, type UploadProgressHandler } from './fileUpload';

// ---------------------------------------------------------------------------
// Suppliers / vendors module (CLE-151) — API client + colocated types.
//
// Mirrors the backend contract in backend/src/routes/suppliers.ts. DB-first
// CRUD; response envelopes follow the app-wide { success, data, meta } shape.
// ---------------------------------------------------------------------------

// Supplier categories are a settings-managed nomenclature (Settings →
// Categorii furnizori), not a hard-coded enum. `Supplier.category` holds the
// category *code*; labels come from GET /api/suppliers/categories.
export type SupplierCategory = string;

export interface SupplierCategoryDef {
  code: string;
  label: string;
}

/** Seed defaults, used as a render fallback while the list loads. */
export const DEFAULT_SUPPLIER_CATEGORIES: SupplierCategoryDef[] = [
  { code: 'saas', label: 'SaaS / Software' },
  { code: 'utilities', label: 'Utilities' },
  { code: 'office', label: 'Office' },
  { code: 'subcontractor', label: 'Subcontractor' },
  { code: 'other', label: 'Other' },
];

/** Code -> label lookup with graceful fallback to the raw code. */
export function supplierCategoryLabel(code: string, categories?: SupplierCategoryDef[]): string {
  const list = categories && categories.length > 0 ? categories : DEFAULT_SUPPLIER_CATEGORIES;
  return list.find((c) => c.code === code)?.label ?? code;
}

export interface Supplier {
  id: string;
  name: string;
  cui: string | null;
  iban: string | null;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  category: SupplierCategory;
  isRecurring: boolean;
  notes: string | null;
  isActive: boolean;
  // Multi-company: firm tag (NULL = default firm).
  companyId: string | null;
  company: { id: string; name: string } | null;
  // Default expense category preselected on bank-statement reconciliation.
  expenseCategoryId: string | null;
  // List endpoint only: paid to this supplier per confirmed bank-statement lines.
  totalSpent?: number;
  totalSpentCurrency?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface SupplierListResponse {
  success: true;
  data: Supplier[];
  meta: SupplierListMeta;
}

export interface SupplierFilters {
  search?: string;
  active?: 'true' | 'false';
  recurring?: 'true' | 'false';
  category?: SupplierCategory;
  // Firm scope (multi-company): undefined = all firms.
  companyId?: string;
  sort?: SupplierSortToken;
  page?: number;
  limit?: number;
}

/** Columns of the suppliers list that the server can order by. */
export type SupplierSortField =
  | 'name'
  | 'cui'
  | 'category'
  | 'isRecurring'
  | 'isActive'
  | 'totalSpent'
  | 'createdAt'
  | 'updatedAt';
export type SupplierSortToken = SupplierSortField | `-${SupplierSortField}`;

export interface SupplierInput {
  name: string;
  cui?: string;
  iban?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  category?: SupplierCategory;
  isRecurring?: boolean;
  notes?: string;
  isActive?: boolean;
  // Multi-company: firm tag; null = default firm.
  companyId?: string | null;
  // Default expense category; null clears it.
  expenseCategoryId?: string | null;
}

// --- Resources (CLE-249) ---------------------------------------------------
// Supplier document folders use Romanian category names to match the unified
// folder explorer UI (CLE-248).
export const SUPPLIER_RESOURCE_CATEGORIES = ['Contracte', 'Facturi', 'Comenzi', 'Altele'] as const;
export type SupplierResourceCategory = (typeof SUPPLIER_RESOURCE_CATEGORIES)[number];

export interface SupplierResource {
  id: string;
  supplierId: string;
  name: string;
  description: string | null;
  category: SupplierResourceCategory;
  fileType: string | null;
  fileSize: number | null;
  s3Key: string;
  s3Url: string | null;
  uploadedAt: string;
}

export interface UploadUrlResult {
  uploadUrl: string;
  key: string;
  expiresIn?: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQuery(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listSuppliers(filters: SupplierFilters = {}): Promise<SupplierListResponse> {
  return apiFetch<SupplierListResponse>(
    `/api/suppliers${buildQuery(filters as Record<string, unknown>)}`,
    { headers: authHeaders() },
  );
}

export async function getSupplier(id: string): Promise<Supplier> {
  const res = await apiFetch<Envelope<Supplier>>(`/api/suppliers/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createSupplier(input: SupplierInput): Promise<Supplier> {
  const res = await apiFetch<Envelope<Supplier>>('/api/suppliers', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateSupplier(id: string, input: SupplierInput): Promise<Supplier> {
  const res = await apiFetch<Envelope<Supplier>>(`/api/suppliers/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function getSupplierCategories(): Promise<SupplierCategoryDef[]> {
  const res = await apiFetch<Envelope<SupplierCategoryDef[]>>('/api/suppliers/categories', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function updateSupplierCategories(
  categories: SupplierCategoryDef[],
): Promise<SupplierCategoryDef[]> {
  const res = await apiFetch<Envelope<SupplierCategoryDef[]>>('/api/suppliers/categories', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ categories }),
  });
  return res.data;
}

export async function deleteSupplier(id: string): Promise<Supplier> {
  const res = await apiFetch<Envelope<Supplier>>(`/api/suppliers/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

/** Soft-deletes a batch of suppliers (isActive=false); unknown ids are skipped. */
export async function bulkDeleteSuppliers(
  ids: string[],
): Promise<{ requested: number; deactivated: number }> {
  const res = await apiFetch<Envelope<{ requested: number; deactivated: number }>>(
    '/api/suppliers/bulk-delete',
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ids }) },
  );
  return res.data;
}

// --- Resources (CLE-249) ---------------------------------------------------

export async function listSupplierResources(supplierId: string): Promise<SupplierResource[]> {
  const res = await apiFetch<Envelope<SupplierResource[]>>(
    `/api/suppliers/${supplierId}/resources`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function requestSupplierResourceUploadUrl(
  supplierId: string,
  input: {
    fileName: string;
    category: SupplierResourceCategory;
    contentType: string;
    fileSize?: number;
  },
): Promise<UploadUrlResult> {
  const res = await apiFetch<Envelope<UploadUrlResult>>(
    `/api/suppliers/${supplierId}/resources/upload-url`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function createSupplierResource(
  supplierId: string,
  input: {
    name: string;
    category: SupplierResourceCategory;
    s3Key: string;
    description?: string;
    fileType?: string;
    fileSize?: number;
  },
): Promise<SupplierResource> {
  const res = await apiFetch<Envelope<SupplierResource>>(`/api/suppliers/${supplierId}/resources`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteSupplierResource(resourceId: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(
    `/api/suppliers/resources/${resourceId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

// Direct upload of raw bytes to a signed upload URL (shared flow with clients).
export async function uploadToSignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: UploadProgressHandler,
): Promise<void> {
  await putFileWithProgress(uploadUrl, file, contentType, onProgress);
}
